import { cloneDeep, get, isArray, set } from 'lodash';
import { ANTRAGS_FORMULAREN } from '../../../../../lib/constants/constant';
import { FormulasResultType } from '../../../../../lib/types/types';
import { addIndexesToPath, groupKostenByPosition } from '../../../../../utils/helper';
import { calculateProjektDauer, calculateProjektStartJahr, ersteVerbundPartnerAnlegen } from '../commonFormulas';
import { executeLoeweSkizzeFormulas } from './LoeweSkizzeFormulas';

// import Schemas
import AntragSchema from '../../../../../lib/paths/antrag_FULL_Paths.json';
import KostenPositionSchema from '../../../../../lib/paths/kostenposition_FULL_Paths.json';
const P = AntragSchema.attributes;
const KP_P = KostenPositionSchema.attributes;

export const executeSkizzeFormulas = (application: any, kostenKategories: any): FormulasResultType[] => {
	const antragFormula = get(application, P.Allgemein_Antragsformular.path, '');

	const vonTag = get(application, P.Foerderprogramm.schema.attributes.Konfiguration_Projektjahr_Von_45_bis_Block_1_Zelle_1, 1);
	const vonMonat = get(application, P.Foerderprogramm.schema.attributes.Konfiguration_Projektjahr_Von_45_bis_Block_1_Zelle_2, 'November');
	const bisTag = get(application, P.Foerderprogramm.schema.attributes.Konfiguration_Projektjahr_Von_45_bis_Block_2_Zelle_1, 31);
	const bisMonat = get(application, P.Foerderprogramm.schema.attributes.Konfiguration_Projektjahr_Von_45_bis_Block_2_Zelle_2, 'Oktober');

	switch (antragFormula) {
		case ANTRAGS_FORMULAREN.EmobFundE:
			return executeEmobSkizzeFormulas(application, kostenKategories, vonTag, vonMonat, bisTag, bisMonat);
		case ANTRAGS_FORMULAREN.Loewe:
			return executeLoeweSkizzeFormulas(application, kostenKategories, vonTag, vonMonat, bisTag, bisMonat);
		default:
			return executeEmobSkizzeFormulas(application, kostenKategories, vonTag, vonMonat, bisTag, bisMonat);
	}
};

const executeEmobSkizzeFormulas = (application: any, kostenKategories: any, vonTag: number, vonMonat: string, bisTag: number, bisMonat: string) => {
	let appCopy = cloneDeep(application);
	let result: FormulasResultType[] = [];
	const KostenKategoriesGroup = groupKostenByPosition(kostenKategories);

	let calcultedProjkJahr = calculateProjektStartJahr(appCopy, vonTag, vonMonat);
	let calcultedProjkdauer = calculateProjektDauer(appCopy, vonTag, vonMonat, bisTag, bisMonat);

	if (isArray(calcultedProjkJahr)) {
		for (let ele of calcultedProjkJahr) {
			set(appCopy, get(ele, 'path'), get(ele, 'value'));
		}
	}
	if (isArray(calcultedProjkdauer)) {
		for (let ele of calcultedProjkdauer) {
			set(appCopy, get(ele, 'path'), get(ele, 'value'));
		}
	}
	result.push(...calcultedProjkJahr);
	result.push(...calcultedProjkJahr);

	let erstePartnerAnlegen = ersteVerbundPartnerAnlegen(appCopy);
	if (isArray(erstePartnerAnlegen)) {
		for (let ele of erstePartnerAnlegen) {
			set(appCopy, get(ele, 'path'), get(ele, 'value'));
		}
	}
	result.push(...erstePartnerAnlegen);

	if (get(appCopy, P.Allgemein_Projektdauerin_Jahren.path) > 0) {
		if (get(KostenKategoriesGroup, 'Ausgabe', []).includes('Personalkosten')) {
			let calculatedPersonalKosten = calculatePersonalKostenSkizze(appCopy);

			if (isArray(calculatedPersonalKosten)) {
				for (let ele of calculatedPersonalKosten) {
					set(appCopy, get(ele, 'path'), get(ele, 'value'));
				}
			}
			result.push(...calculatedPersonalKosten);
		}

		let calculatedSummeProOrganisation = calculateSummeProOrganisation(appCopy);

		if (isArray(calculatedSummeProOrganisation)) {
			for (let ele of calculatedSummeProOrganisation) {
				set(appCopy, get(ele, 'path'), get(ele, 'value'));
			}
		}
		result.push(...calculatedSummeProOrganisation);

		let calculatedGesamtSummeSkizze = calculateGesamtSummenSkizze(appCopy);

		if (isArray(calculatedGesamtSummeSkizze)) {
			for (let ele of calculatedGesamtSummeSkizze) {
				set(appCopy, get(ele, 'path'), get(ele, 'value'));
			}
		}
		result.push(...calculatedGesamtSummeSkizze);
	}

	return result;
};

function calculatePersonalKostenSkizze(application: any) {
	let result = [];

	const allKostenPosition = get(application, P.Foerderantrag_Kostenpositions.path, []);
	for (let i = 0; i < allKostenPosition.length; i++) {
		const eachKostenPosition = allKostenPosition[i];
		if (get(eachKostenPosition, KP_P.Allgemein_Kostenart_Finanzierung.path) === 'Personalkosten') {
			const StundenSatz = get(eachKostenPosition, KP_P.Personalausgaben_Emob_Skizze.schema.attributes.E_Mob_Skizze_Stundensatz.path, 0);
			const Geplantestunde = get(
				eachKostenPosition,
				KP_P.Personalausgaben_Emob_Skizze.schema.attributes.E_Mob_Skizze_Geplante_Stunden_Im_Projekt.path,
				0
			);

			const geplaneSumme = parseFloat(StundenSatz) * parseFloat(Geplantestunde);
			result.push({
				path: addIndexesToPath(
					P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Geplante_Gesamtsumme.path,
					[`${i}`],
					['Foerderantrag_Kostenpositions']
				),
				value: !isNaN(geplaneSumme) ? geplaneSumme.toFixed(2) : 0,
			});
		}
	}
	return result;
}
function calculateSummeProOrganisation(application: any) {
	let result: FormulasResultType[] = [];
	const allKostenPosition = get(application, P.Foerderantrag_Kostenpositions.path, []);

	const allAusgabenAntragsteller_Arr = allKostenPosition.filter(
		(ele: any) =>
			get(ele, `${KP_P.Unternehmen.path}.id`) === get(application, `${P.Antragstellende_Organisation.path}.id`) &&
			get(ele, KP_P.Allgemein_Kostenposition.path) === 'Ausgabe'
	);
	const allEinnahmenAntragsteller_Arr = allKostenPosition.filter(
		(ele: any) =>
			get(ele, `${KP_P.Unternehmen.path}.id`) === get(application, `${P.Antragstellende_Organisation.path}.id`) &&
			get(ele, KP_P.Allgemein_Kostenposition.path) === 'Einnahme'
	);

	const ausgabenGesamtAntragsteller = allAusgabenAntragsteller_Arr.reduce((acc: any, ele: any) => {
		return acc + parseFloat(get(ele, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0.0'));
	}, 0);
	const einnahmenGesamtAntragsteller = allEinnahmenAntragsteller_Arr.reduce((acc: any, ele: any) => {
		return acc + parseFloat(get(ele, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0.0'));
	}, 0);
	const zuwendungGesamtAntragsteller = ausgabenGesamtAntragsteller - einnahmenGesamtAntragsteller;
	const foerderQuoteAntragsteller = (zuwendungGesamtAntragsteller / ausgabenGesamtAntragsteller) * 100;

	result.push({
		path: P.Orga_Foerderquote_Gesamt.path,
		value: !isNaN(foerderQuoteAntragsteller) ? foerderQuoteAntragsteller.toFixed(2) : 0,
	});

	const allVerbundPartner = get(application, P.Verbundpartner.path, []);

	allVerbundPartner.forEach((eachPartner: any, i: number) => {
		const eachPartnerId = get(eachPartner, 'Verbundpartner.id');
		if (eachPartnerId) {
			const allAusgabenEachPartner_Arr = allKostenPosition.filter(
				(ele: any) => get(ele, `${KP_P.Unternehmen.path}.id`) === eachPartnerId && get(ele, KP_P.Allgemein_Kostenposition.path) === 'Ausgabe'
			);
			const allEinnahmenEachPartnerr_Arr = allKostenPosition.filter(
				(ele: any) => get(ele, `${KP_P.Unternehmen.path}.id`) === eachPartnerId && get(ele, KP_P.Allgemein_Kostenposition.path) === 'Einnahme'
			);

			const ausgabenGesamtEachPartner = allAusgabenEachPartner_Arr.reduce((acc: any, ele: any) => {
				return acc + parseFloat(get(ele, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0.0'));
			}, 0);
			const einnahmenGesamtEachPartner = allEinnahmenEachPartnerr_Arr.reduce((acc: any, ele: any) => {
				return acc + parseFloat(get(ele, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0.0'));
			}, 0);
			const zuwendungGesamtEachPartner = ausgabenGesamtEachPartner - einnahmenGesamtEachPartner;
			const foerderQuoteEachPartner = (zuwendungGesamtEachPartner / ausgabenGesamtEachPartner) * 100;

			result.push({
				path: `${P.Verbundpartner.path}.${i}.Foerderquote_Gesamt_403741`,
				value: !isNaN(foerderQuoteEachPartner) ? foerderQuoteEachPartner.toFixed(2) : 0,
			});
		}
	});

	return result;
}
function calculateGesamtSummenSkizze(application: any) {
	let result: any = [];
	const allKostenPosition = get(application, P.Foerderantrag_Kostenpositions.path, []);

	let ProjektAusgabenGesamt = 0;
	let ProjektEinnahmenGesamt = 0;
	let BeantragteZuwendungGesamt = 0;
	let FoerderquoteGesamt = 0;

	for (let i = 0; i < allKostenPosition.length; i++) {
		const eachKostenPosition = allKostenPosition[i];

		if (get(eachKostenPosition, KP_P.Allgemein_Kostenposition.path, '') === 'Ausgabe') {
			ProjektAusgabenGesamt += parseFloat(get(eachKostenPosition, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0.0'));
		}
		if (get(eachKostenPosition, KP_P.Allgemein_Kostenposition.path, '') === 'Einnahme') {
			ProjektEinnahmenGesamt += parseFloat(get(eachKostenPosition, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0.0'));
		}
	}

	BeantragteZuwendungGesamt = ProjektAusgabenGesamt - ProjektEinnahmenGesamt;
	FoerderquoteGesamt = (BeantragteZuwendungGesamt / ProjektAusgabenGesamt) * 100;

	result.push(
		{
			path: P.Allgemein_Geplante_Projektausgaben.path,
			value: !isNaN(ProjektAusgabenGesamt) ? ProjektAusgabenGesamt.toFixed(2) : 0,
		},
		{
			path: P.Allgemein_Eingebrachte_Eigen_Und_Drittmittel.path,
			value: !isNaN(ProjektEinnahmenGesamt) ? ProjektEinnahmenGesamt.toFixed(2) : 0,
		},
		{
			path: P.Allgemein_Beantragte_Zuwendung.path,
			value: !isNaN(BeantragteZuwendungGesamt) ? BeantragteZuwendungGesamt.toFixed(2) : 0,
		},
		{ path: P.Bewilligung_Foerderquote_Gesamt.path, value: !isNaN(FoerderquoteGesamt) ? FoerderquoteGesamt.toFixed(2) : 0 },
		{ path: P.Allgemein_Beantragte_Zuwendung.path, value: !isNaN(FoerderquoteGesamt) ? FoerderquoteGesamt.toFixed(2) : 0 }
	);

	return result;
}
